import { observer } from 'mobx-react-lite'
import { CircularProgress, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { UserWithMatchedSkills, ProjectSkill } from '../../model/project-builder'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { useState } from 'react'
import { CreateProjectModal } from './CreateProjectModal'
import { MatchedUsersList } from './MatchedUsersList'
import { ColorTokens, Palette, RadiusTokens } from 'src/feature/theme/model/Palette'
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded'

interface ProjectBuilderMatchedUsersProps {
  usersWithUsedSkills: UserWithMatchedSkills[]
  usersWithUnusedSkills: UserWithMatchedSkills[]
  gaps: ProjectSkill[]
  selectedSkill?: string
}

export const ProjectBuilderMatchedUsers = observer(({
  usersWithUsedSkills,
  usersWithUnusedSkills,
  gaps,
  selectedSkill,
}: ProjectBuilderMatchedUsersProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false)

  if (!selectedSkill) {
    return null
  }

  // For filtering matched users by selected skills
  const filteredUsersWithUsedSkills = selectedSkill
    ? usersWithUsedSkills.filter(user => user.matchedSkills.some(skill => skill.projectSkills.includes(selectedSkill)))
    : []

  const filteredUsersWithUnusedSkills = selectedSkill
    ? usersWithUnusedSkills.filter(user => user.matchedSkills.some(skill => skill.projectSkills.includes(selectedSkill)))
    : []

  const hasAnyUsers = usersWithUsedSkills.length > 0 || usersWithUnusedSkills.length > 0

  const hasAnyFilteredUsers = filteredUsersWithUsedSkills.length > 0 || filteredUsersWithUnusedSkills.length > 0

  if (!hasAnyUsers && !gaps.length) {
    return null
  }

  const selectUser = (userId: string) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }

      return newSet
    })
  }

  const handleBuildProject = () => {
    setShowCreateProjectModal(true)
  }

  const handleCloseModal = () => {
    setShowCreateProjectModal(false)
    setSelectedUsers(new Set())
  }

  return (
    <AppBox sx={styles.container}>
      <AppPaper sx={styles.mainContent}>
        
        {hasAnyFilteredUsers ? (
          <>
          
            <MatchedUsersList
              usersWithUsedSkills={filteredUsersWithUsedSkills}
              usersWithUnusedSkills={filteredUsersWithUnusedSkills}
              selectedUsers={selectedUsers}
              onSelectUser={selectUser}
              selectedSkill={selectedSkill}
            />

            <AppBox sx={styles.bottomActionRow}>
              <AppButton
                fontSize={14}
                variant='contained'
                onClick={handleBuildProject}
                disabled={selectedUsers.size === 0}
                endIcon={showCreateProjectModal ? <CircularProgress size={16} color='inherit' sx={{ margin: '0px 3px' }} /> : null}
              >
                {projectBuilderTranslation.addToProjectButton()}
              </AppButton>

              <AppBox sx={styles.addIconWrapper}>
                <ArchiveRoundedIcon />
                
                {selectedUsers.size > 0 && (
                  <AppBox sx={styles.selectedUsersBadge}>
                    <Typography sx={styles.selectedUsersText}>
                      {selectedUsers.size}
                    </Typography>
                  </AppBox>
                )}
              </AppBox>
            </AppBox>
          </>
        ) : (
          <AppBox sx={styles.gapCard}>

            <AppBox sx={styles.content}>
              <Typography sx={styles.gapBadge}>{projectBuilderTranslation.note()}</Typography>
              <Typography variant='h2'>{projectBuilderTranslation.gaps()}</Typography>
              <AppBox sx={styles.divider} />
              <Typography variant='body2' sx={styles.suggestion}>{projectBuilderTranslation.suggestion()}</Typography>
            </AppBox>
          </AppBox>
        )}
      </AppPaper>
      <CreateProjectModal
        open={showCreateProjectModal}
        onClose={handleCloseModal}
        selectedUserIds={selectedUsers}
        allUsers={[...usersWithUsedSkills, ...usersWithUnusedSkills]}
      />
    </AppBox>
  )
})

const styles = {
  container: {
    position: 'static',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
  },
  mainContent: {
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    gap: 20,
    width: '100%',
    border: 0,
  },
  gapCard: {
    position: 'relative',
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  content: {
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  gapBadge: {
    alignSelf: 'flex-start',
    backgroundColor: ColorTokens.primary200,
    borderRadius: RadiusTokens.pill,
    padding: '5px 10px',
    fontSize: 12,
    lineHeight: 1.2,
    color: 'background.default',
    textAlign: 'center',
  },
  divider: {
    height: '1px',
    backgroundColor: ColorTokens.neutral300,
    width: '100%',
  },
  suggestion: {
    color: 'warning.main',
  },
  bottomActionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 15px',
  },
  addIconWrapper: {
    position: 'relative',
    width: 35,
    height: 35,
    borderRadius: '50%',
    border: `2px solid ${Palette.CORAL_REEF_GREEN}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  selectedUsersBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translate(40%, -40%)',
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: '#E5484D',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedUsersText: {
    color: ColorTokens.neutral100,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
}